import dayjs from 'dayjs'
import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import type { CloseButtonProps } from 'react-toastify/dist/components/CloseButton'
import { Button } from 'reactstrap'
import { invalidateQueries } from '../../../api/helpers'
import notifications from '../../../api/notifications/hooks'
import { NotificationActivity } from '../../../api/notifications/types'
import { selectIsAuthenticated } from '../../../store/auth'

const STORAGE_KEY = 'shownDeliveryIds'

const getShownDeliveryIds = (): Set<number> => {
    try {
        return new Set(JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? '[]'))
    } catch {
        return new Set()
    }
}

const addShownDeliveryId = (deliveryId: number) => {
    const stored = getShownDeliveryIds()
    stored.add(deliveryId)
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...stored]))
}

const CloseButton = ({ closeToast }: CloseButtonProps) => <Button close onClick={closeToast} style={{ alignSelf: 'center' }} />

const useActivityReminderToasts = (enabled: boolean) => {
    const isAuthenticated = useSelector(selectIsAuthenticated)

    const { data } = notifications.GetNotificationDeliveries.useQuery(
        {
            'filter[notification.code]': 'activity.reminder',
            'filter[status]': ['unread'],
        },
        {
            enabled: enabled,
            refetchInterval: 60000,
        }
    )

    useEffect(() => {
        if (!isAuthenticated) sessionStorage.removeItem(STORAGE_KEY)
    }, [isAuthenticated])

    useEffect(() => {
        if (data === undefined) return

        const items = (data?.data?.items ?? []) as NotificationActivity[]
        const shownDeliveryIds = getShownDeliveryIds()

        items.forEach((item) => {
            if (shownDeliveryIds.has(item.deliveryId)) return

            const now = dayjs()
            const activityTime = dayjs(`${item.activity.dateFrom} ${item.activity.timeFrom}`)
            const reminderTime = activityTime.subtract(item.activity.minutesBefore, 'minute')

            // Only show the toast if the current time is between the reminder time and the activity time
            // For activities that are in the past, we don't show the toast, but only notifications in dropdown
            if (now.isBefore(reminderTime) || now.isAfter(activityTime)) return

            addShownDeliveryId(item.deliveryId)

            invalidateQueries(['notifications', 'deliveries', 'unread-count'])

            toast.info(item.message, {
                toastId: `activity-reminder-${item.deliveryId}`,
                autoClose: false,
                closeButton: CloseButton,
            })
        })
    }, [data])
}

export default useActivityReminderToasts
